import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatRON, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, removeItem, total } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    if (!user) {
      toast.error("Autentifică-te pentru a finaliza comanda");
      navigate("/autentificare");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  };

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <ShoppingBag className="w-16 h-16 mx-auto text-slate-300" />
        <h1 className="text-2xl font-black tracking-tight mt-6">Coșul tău este gol</h1>
        <p className="text-slate-500 mt-2">Descoperă mașinile și consumabilele noastre.</p>
        <Link to="/produse" className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 font-bold uppercase tracking-wide text-sm mt-8 transition-colors" data-testid="empty-cart-cta">
          Vezi produsele <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-6 py-12">
      <h1 className="text-4xl font-black tracking-tight text-slate-900 mb-10">Coș de cumpărături</h1>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 divide-y divide-slate-200 border-y border-slate-200">
          {items.map((i) => (
            <div key={i.product_id} className="flex gap-5 py-5" data-testid={`cart-item-${i.product_id}`}>
              <img src={i.image} alt={i.name} className="w-24 h-24 object-cover border border-slate-200 bg-slate-100" />
              <div className="flex-1 min-w-0">
                <Link to={`/produse/${i.product_id}`} className="font-bold text-slate-900 hover:text-orange-600 line-clamp-2">{i.name}</Link>
                <div className="text-sm text-slate-500 mt-1">{formatRON(i.price)} / buc</div>
                <div className="flex items-center gap-4 mt-3">
                  <div className="flex items-center border border-slate-300">
                    <button onClick={() => updateQty(i.product_id, i.quantity - 1)} className="p-2 hover:bg-slate-100"><Minus className="w-3.5 h-3.5" /></button>
                    <span className="w-10 text-center text-sm font-bold">{i.quantity}</span>
                    <button onClick={() => updateQty(i.product_id, i.quantity + 1)} className="p-2 hover:bg-slate-100"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                  <button onClick={() => removeItem(i.product_id)} className="text-slate-400 hover:text-red-600 transition-colors" data-testid={`remove-${i.product_id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="text-right font-black text-slate-900">{formatRON(i.price * i.quantity)}</div>
            </div>
          ))}
        </div>
        <div>
          <div className="border border-slate-200 p-6 sticky top-28">
            <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-5">Sumar comandă</h2>
            <div className="flex justify-between text-sm mb-3"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{formatRON(total)}</span></div>
            <div className="flex justify-between text-sm mb-5"><span className="text-slate-500">Transport</span><span className="font-semibold">Calculat la livrare</span></div>
            <div className="flex justify-between border-t border-slate-200 pt-5 mb-6">
              <span className="font-bold">Total</span>
              <span className="text-2xl font-black text-slate-900" data-testid="cart-total">{formatRON(total)}</span>
            </div>
            <button onClick={checkout} disabled={loading}
              className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-60 text-white px-6 py-4 font-bold uppercase tracking-wide text-sm transition-colors" data-testid="checkout-button">
              {loading ? "Se procesează..." : "Finalizează comanda"}
            </button>
            <p className="text-xs text-slate-400 mt-3 text-center">Plată securizată prin Stripe</p>
          </div>
        </div>
      </div>
    </div>
  );
}
